import React from 'react';
import {DoppeDtoAction, DoppeDtoActionViewType} from '../../client-server-common/types/doppe-dto-action';
import {DoppeActionWidgetOpeningMode, useBymoPageProps} from '../../client-server-common';
import {DoppeViewerActionRenderer} from '../doppe-viewer-action-renderer/doppe-viewer-action-renderer';
import {evaluateFunction, evaluateWhenFunction, tsTypeAssert} from '@wix/devzai-utils-common';
import {BymoActionsMode} from '../../client-server-common/bymo-pages/bymo-actions-settings';
import {DoppeActionView} from '../../doppe-sdk';
import {DoppeViewerActionContext} from '../doppe-viewer-action-context/doppe-viewer-action-context';
import {classes, style} from './doppe-viewer-action.st.css';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {
    useBymoPageViewerComponentsContext
} from '../bymo-page-viewer-components-context/bymo-page-viewer-components-context';

export const DoppeViewerAction = React.memo(function DoppeViewerAction(props: DoppeViewerAction.Props) {
    const {
        action,
        renderActionView,
        supportsRenderingFramelessWidgets = false,
        isExpanded = false,
        actionTogglingController,
        fullScreenModeTogglingController,
        actionIndex,
        actionViewRootHtmlAttributes
    } = props;

    const bymoPageProps = useBymoPageProps();
    const pageViewerContext = useBymoPageViewerContext();
    const bymoPageViewerComponentsContextData = useBymoPageViewerComponentsContext();


    const {
        actionsSettings
    } = bymoPageProps;

    return (
        <DoppeViewerActionRenderer
            action={action}
            actionIndex={actionIndex}
            actionTogglingController={actionTogglingController}
            fullScreenModeTogglingController={fullScreenModeTogglingController}
        >
            {(actionContext, actionType, observableActionContext) => {

                const canRenderInPage = actionType.canRenderInPage ?? false;
                const shouldOpenCTA = actionContext.action.widgetOpeningMode === DoppeActionWidgetOpeningMode.OpenCTA;

                const viewType = evaluateFunction(() => {

                    if (!canRenderInPage) {
                        return DoppeDtoActionViewType.ActionButton;
                    } else if (supportsRenderingFramelessWidgets && actionsSettings.actionsMode === BymoActionsMode.SingleAction && actionType.renderWidget !== undefined) {
                        return DoppeDtoActionViewType.InPageFrameless;
                    } else {
                        return actionContext.getActionViewType();
                    }
                })

                const renderActionWidget = (htmlAttributes: React.HTMLAttributes<any>) => {
                    if (actionType.renderWidget === undefined) {
                        return (
                            <div>{`Action type with id '${action.actionTypeId}' doesn't support inline rendering.`}</div>
                        )
                    } else {
                        return actionType.renderWidget({
                            ...htmlAttributes,
                            actionContext: actionContext
                        }, bymoPageViewerComponentsContextData.actionTypesViewerComponents.get(actionType.id));
                    }
                }

                const actionButtonProps = actionType.getActionButtonProps?.(actionContext, observableActionContext) ?? {};
                const activateCTA = () => {
                    if (!actionType.activateCTA) {
                        console.log(`Action type with id '${action.actionTypeId}' doesn't support CTA activation.`);
                    } else {
                        actionType.activateCTA(actionContext, observableActionContext);
                    }
                }

                const {
                    className,
                    ...restActionViewRootHtmlAttributes
                } = {
                    ...actionViewRootHtmlAttributes
                }

                const actionViewContextProps = tsTypeAssert<DoppeActionView.ActionContextProps>({
                    label: action.name,
                    actionImage: action.icon,
                    actionDescription: action.callToActionDescription,
                    viewType: viewType,
                    renderActionWidget: renderActionWidget,
                    actionButtonComponentProps: actionButtonProps,
                    activateCTA: activateCTA,
                    isWidgetOpener: (evaluateWhenFunction(actionType.isWidgetOpener, actionContext) && !shouldOpenCTA) ?? false,
                    skipReportingOnGoalAchievedOnActionButtonClick: evaluateWhenFunction(actionType.skipReportingOnGoalAchievedOnActionButtonClick, actionContext) ?? false,
                    actionContext: actionContext,
                    isExpanded: isExpanded,
                    actionViewRootHtmlAttributes: {
                        className: style(classes.root, {previewMode: pageViewerContext.mode === BymoPageViewerMode.Preview}, className),
                        ...restActionViewRootHtmlAttributes
                    },
                    showActionHeader: action.showActionHeader !== undefined ? action.showActionHeader : true,
                });

                return renderActionView({
                    actionViewContextProps: actionViewContextProps
                })
            }}
        </DoppeViewerActionRenderer>
    )
});

export namespace DoppeViewerAction {

    export interface ActionViewRenderProps {
        actionViewContextProps: DoppeActionView.ActionContextProps;
    }

    export type ActionViewRenderFunction = (renderProps: ActionViewRenderProps) => React.ReactElement;

    export interface Props {
        action: DoppeDtoAction;
        renderActionView: ActionViewRenderFunction;
        supportsRenderingFramelessWidgets?: boolean;
        isExpanded?: boolean;
        actionTogglingController?: DoppeViewerActionContext.ActionTogglingController;
        fullScreenModeTogglingController?: DoppeViewerActionContext.FullScreenModeTogglingController;
        actionIndex?: number;
        actionViewRootHtmlAttributes?: React.HTMLAttributes<any>
    }

}